"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  useTheme,
  Paper,
} from "@mui/material"
import {
  Anchor as AnchorIcon,
  Close as CloseIcon,
  Security as SecurityIcon,
  Notifications as NotificationsIcon,
  Terminal as TerminalIcon,
  Warning as WarningIcon,
  CheckCircle,
} from "@mui/icons-material"
import { devicesActions, geofencesActions } from '../../store';

const RemoveSuccessToast = ({ open, message, onClose }) => {
  const theme = useTheme()
  const [progress, setProgress] = useState(0)
  const isDarkMode = theme.palette.mode === "dark"

  useEffect(() => {
    if (!open) return setProgress(0)

    const timer = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + 2.5
        return newProgress >= 100 ? 100 : newProgress
      })
    }, 100)

    return () => clearInterval(timer)
  }, [open])

  useEffect(() => {
    if (progress >= 100) setTimeout(onClose, 200)
  }, [progress, onClose])

  if (!open) return null

  return createPortal(
    <Box sx={{ position: "fixed", top: 20, right: 20, zIndex: 9999, minWidth: 300 }}>
      <Paper elevation={8} sx={{
        bgcolor: isDarkMode ? "#d32f2f" : "#fff",
        color: isDarkMode ? "#fff" : "#333",
        border: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(211,47,47,0.2)"
      }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" p={1.5} pr={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <CheckCircle color={isDarkMode ? "inherit" : "error"} />
            <Typography fontWeight={500}>{message}</Typography>
          </Box>
          <IconButton size="small" onClick={onClose} color={isDarkMode ? "inherit" : "default"}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <LinearProgress variant="determinate" value={progress} sx={{
          height: 4,
          bgcolor: isDarkMode ? "rgba(255,255,255,0.2)" : "rgba(211,47,47,0.1)",
          "& .MuiLinearProgress-bar": {
            bgcolor: isDarkMode ? "#ffcdd2" : "#d32f2f",
          },
        }} />
      </Paper>
    </Box>,
    document.body
  )
}

export default function RemoveAnchorDialog({ open, onClose, deviceName, device, position, dispatch }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [showSuccessToast, setShowSuccessToast] = useState(false)
  const [toastMessage, setToastMessage] = useState("")
  const isBlocked = position?.attributes?.blocked || false

  const playRemoveSound = () => {
    try {
      const audio = new Audio("./resources/removeFence.mp3")
      audio.volume = 0.7
      audio.play().catch(() => {
        new Audio("./src/resources/removeFence.mp3").play().catch(console.warn)
      })
    } catch (error) {
      console.warn("Erro no áudio:", error)
    }
  }

  const refreshAll = async () => {
    try {
      const [geofencesRes, devicesRes] = await Promise.all([
        fetch('/api/geofences'),
        fetch('/api/devices')
      ])

      if (geofencesRes.ok) dispatch(geofencesActions.refresh(await geofencesRes.json()))
      if (devicesRes.ok) dispatch(devicesActions.refresh(await devicesRes.json()))
    } catch (error) {
      console.error("Erro ao atualizar estados:", error)
    }
  }

  const handleRemove = async () => {
    if (!device?.id) return setError("Dispositivo inválido")

    setLoading(true)
    setError("")

    try {
      // 1. Executar operações em paralelo quando possível
      const operations = []

      // 2. Desbloquear se necessário
      if (isBlocked) {
        operations.push(
          fetch('/api/commands/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ deviceId: device.id, type: 'engineResume' })
          })
        )
      }

      // 3. Remover recursos se existirem
      const fenceId = device.attributes?.fence_id
      if (fenceId && fenceId !== "0") {
        operations.push(
          fetch(`/api/geofences/${fenceId}`, { method: "DELETE" }),
          fetch("/api/permissions", {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ deviceId: device.id, geofenceId: fenceId })
          })
        )
      }

      // 4. Atualizar dispositivo
      operations.push(
        fetch(`/api/devices/${device.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...device,
            attributes: {
              ...device.attributes,
              lockOnExit: false,
              hasAnchor: false,
              fence_id: "0",
              blocked: false
            }
          })
        })
      )

      await Promise.all(operations)
      await refreshAll()

      // Feedback
      playRemoveSound()
      setToastMessage(`Âncora removida ${isBlocked ? 'e dispositivo desbloqueado ' : ''}de ${deviceName}!`)
      setShowSuccessToast(true)
      setTimeout(onClose, 300)
    } catch (err) {
      console.error("Erro:", err)
      setError(err.message || "Erro na remoção")
      await refreshAll() // Tentar atualizar mesmo com erro
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <AnchorIcon />
              <Typography variant="h6">Remover Âncora</Typography>
            </Box>
            <IconButton onClick={onClose} disabled={loading}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ mb: 2 }}>
            <Typography>Dispositivo:</Typography>
            <Chip label={deviceName} />
            {isBlocked && <Chip label="Bloqueado" color="warning" sx={{ ml: 1 }} />}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body1" gutterBottom>
            Ações que serão executadas:
          </Typography>

          <Box sx={{ pl: 1 }}>
            {[
              { icon: <SecurityIcon fontSize="small" />, text: "Remover geofence" },
              { icon: <NotificationsIcon fontSize="small" />, text: "Limpar notificações" },
              { icon: <TerminalIcon fontSize="small" />, text: "Remover comandos automáticos" },
              ...(isBlocked ? [{ icon: <WarningIcon color="warning" fontSize="small" />, text: "Desbloquear dispositivo" }] : [])
            ].map((item, index) => (
              <Box key={index} display="flex" alignItems="center" gap={2} mb={1.5}>
                {item.icon}
                <Typography>{item.text}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemove}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AnchorIcon />}
          >
            {loading ? "Removendo..." : "Confirmar Remoção"}
          </Button>
        </DialogActions>
      </Dialog>

      <RemoveSuccessToast open={showSuccessToast} message={toastMessage} onClose={() => setShowSuccessToast(false)} />
    </>
  )
}